import React, { createContext, useContext, useReducer, useEffect } from 'react'
import PropTypes from 'prop-types'
import toast from 'react-hot-toast'

const CartContext = createContext()

const initialState = {
  items: [],
  total: 0,
  subtotal: 0,
  vat: 0,
  totalWeight: 0,
  itemCount: 0,
  isLoading: false,
  error: null,
}

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM':
      const existingItem = state.items.find(item => item.id === action.payload.id)
      if (existingItem) {
        const updatedItems = state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: item.quantity + action.payload.quantity }
            : item
        )
        return {
          ...state,
          items: updatedItems,
          total: calculateTotal(updatedItems),
          subtotal: calculateSubtotal(updatedItems),
          vat: calculateVAT(updatedItems),
          totalWeight: calculateTotalWeight(updatedItems),
          itemCount: calculateItemCount(updatedItems),
        }
      } else {
        const newItems = [...state.items, action.payload]
        return {
          ...state,
          items: newItems,
          total: calculateTotal(newItems),
          subtotal: calculateSubtotal(newItems),
          vat: calculateVAT(newItems),
          totalWeight: calculateTotalWeight(newItems),
          itemCount: calculateItemCount(newItems),
        }
      }
    
    case 'REMOVE_ITEM':
      const filteredItems = state.items.filter(item => item.id !== action.payload)
      return {
        ...state,
        items: filteredItems,
        total: calculateTotal(filteredItems),
        subtotal: calculateSubtotal(filteredItems),
        vat: calculateVAT(filteredItems),
        totalWeight: calculateTotalWeight(filteredItems),
        itemCount: calculateItemCount(filteredItems),
      }
    
    case 'UPDATE_QUANTITY':
      const updatedItems = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, quantity: Math.max(0, action.payload.quantity) }
          : item
      ).filter(item => item.quantity > 0)
      
      return {
        ...state,
        items: updatedItems,
        total: calculateTotal(updatedItems),
        subtotal: calculateSubtotal(updatedItems),
        vat: calculateVAT(updatedItems),
        totalWeight: calculateTotalWeight(updatedItems),
        itemCount: calculateItemCount(updatedItems),
      }
    
    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        total: 0,
        subtotal: 0,
        vat: 0,
        totalWeight: 0,
        itemCount: 0,
      }
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    
    case 'LOAD_CART':
      return {
        ...state,
        items: action.payload,
        total: calculateTotal(action.payload),
        subtotal: calculateSubtotal(action.payload),
        vat: calculateVAT(action.payload),
        totalWeight: calculateTotalWeight(action.payload),
        itemCount: calculateItemCount(action.payload),
      }
    
    default:
      return state
  }
}

const calculateTotal = (items) => {
  return items.reduce((total, item) => {
    const itemTotal = item.price * item.quantity
    // Add VAT if the item is VAT chargeable
    const vatAmount = item.vat_chargeable ? (itemTotal * (item.vat_rate / 100)) : 0
    return total + itemTotal + vatAmount
  }, 0)
}

const calculateSubtotal = (items) => {
  return items.reduce((total, item) => total + (item.price * item.quantity), 0)
}

const calculateVAT = (items) => {
  return items.reduce((total, item) => {
    if (item.vat_chargeable) {
      const itemTotal = item.price * item.quantity
      return total + (itemTotal * (item.vat_rate / 100))
    }
    return total
  }, 0)
}

const calculateItemCount = (items) => {
  return items.reduce((count, item) => count + item.quantity, 0)
}

const calculateTotalWeight = (items) => {
  return items.reduce((total, item) => {
    if (item.weight && item.weight_unit) {
      // Convert all weights to grams for consistent calculation
      let weightInGrams = item.weight
      if (item.weight_unit === 'kg') {
        weightInGrams = item.weight * 1000
      } else if (item.weight_unit === 'ml') {
        // For liquids, assume 1ml = 1g (approximate)
        weightInGrams = item.weight
      }
      return total + (weightInGrams * item.quantity)
    }
    return total
  }, 0)
}

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState)

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('maurifood_cart')
      if (savedCart) {
        const cartData = JSON.parse(savedCart)
        // Validate cart data before loading
        if (Array.isArray(cartData)) {
          // Clean up any cart items with malformed image data
          const cleanedCartData = cartData.map(item => {
            // If image contains URL strings that shouldn't be displayed, clean it up
            if (item.image && typeof item.image === 'string' && item.image.includes('r2.dev')) {
              // Extract the first valid image URL or use fallback
              const urls = item.image.split(' ')
              const validUrl = urls.find(url => url.startsWith('https://') && url.includes('.png'))
              return {
                ...item,
                image: validUrl || '🛒'
              }
            }
            return item
          })
          dispatch({ type: 'LOAD_CART', payload: cleanedCartData })
        } else {
          console.warn('Invalid cart data format, clearing localStorage')
          localStorage.removeItem('maurifood_cart')
        }
      }
    } catch (error) {
      console.error('Failed to load cart from localStorage:', error)
      localStorage.removeItem('maurifood_cart')
    }
  }, [])

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('maurifood_cart', JSON.stringify(state.items))
    } catch (error) {
      console.error('Failed to save cart to localStorage:', error)
    }
  }, [state.items])

  const addToCart = (product, quantity = 1) => {
    // Handle different image formats
    let productImage = '🛒' // Default fallback
    
    if (product.images) {
      try {
        // If images is a JSON string, parse it
        if (typeof product.images === 'string' && product.images.startsWith('[')) {
          const parsedImages = JSON.parse(product.images)
          if (Array.isArray(parsedImages) && parsedImages.length > 0) {
            productImage = parsedImages[0]
          }
        } else if (Array.isArray(product.images) && product.images.length > 0) {
          // If images is already an array, take the first one
          productImage = product.images[0]
        } else if (typeof product.images === 'string' && product.images.startsWith('http')) {
          // If images is a single URL string
          productImage = product.images
        }
      } catch (error) {
        console.error('Error parsing product images:', error)
        productImage = '🛒'
      }
    } else if (product.image_url) {
      // If there's an image_url field
      productImage = product.image_url
    } else if (product.image) {
      // If there's an image field
      productImage = product.image
    }

    const cartItem = {
      id: product.id,
      name: product.name,
      price: product.price,
      image: productImage,
      quantity,
      maxQuantity: product.stock_quantity || product.stock || 999,
      description: product.description,
      vat_chargeable: product.vat_chargeable || false,
      vat_rate: product.vat_rate || 0,
      weight: product.weight,
      weight_unit: product.weight_unit || 'grams',
    }
    
    console.log('Adding to cart:', cartItem) // Debug log
    dispatch({ type: 'ADD_ITEM', payload: cartItem })
    toast.success(`${product.name} added to cart!`)
  }

  const removeFromCart = (productId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: productId })
    toast.success('Item removed from cart')
  }

  const updateQuantity = (productId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } })
  }

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' })
    toast.success('Cart cleared')
  }

  const getItemQuantity = (productId) => {
    const item = state.items.find(item => item.id === productId)
    return item ? item.quantity : 0
  }

  const isInCart = (productId) => {
    return state.items.some(item => item.id === productId)
  }

  const value = {
    ...state,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getItemQuantity,
    isInCart,
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

CartProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useCart = () => {
  try {
    const context = useContext(CartContext)
    if (!context) {
      throw new Error('useCart must be used within a CartProvider')
    }
    return context
  } catch (error) {
    console.error('Cart context error:', error)
    // Return a fallback cart context to prevent crashes
    return {
      items: [],
      total: 0,
      subtotal: 0,
      vat: 0,
      itemCount: 0,
      isLoading: false,
      error: error.message,
      addToCart: () => {},
      removeFromCart: () => {},
      updateQuantity: () => {},
      clearCart: () => {},
      getItemQuantity: () => 0,
      isInCart: () => false,
    }
  }
}
